'use client';

import { useEffect, useMemo, useState } from 'react';
import type { ProjectDto, ProjectTypeDto, SampleDto } from '@/api/ApiClient';
import { useBackend } from '@/hooks/useBackend';
import { createProjectResolver } from '@/lib/utils/sampleProjectMultiplicity';
import {
  isInHouseReportingCategoryContext,
  projectIdsMissingFromResolver,
} from '@/lib/utils/inHouseReportingCategoryContext';

export type SampleReportingCategoryProjectOptions = {
  projects: readonly ProjectDto[] | undefined;
  projectTypes: readonly ProjectTypeDto[] | undefined;
  /** True while useSelectOptions is still loading requested keys (including projects). */
  optionsLoading: boolean;
};

type Evaluation =
  | { status: 'hidden' }
  | { status: 'loading' }
  | { status: 'resolved'; visible: boolean }
  | { status: 'fetch-missing'; projectIds: number[]; missingIds: number[] };

function evaluate(
  sample: SampleDto | undefined,
  hasProcessDevAccess: boolean,
  resolveProject: (id: number) => ProjectDto | undefined,
  optionsLoading: boolean
): Evaluation {
  if (!sample || !hasProcessDevAccess) {
    return { status: 'hidden' };
  }
  if ((sample.subSamples?.length ?? 0) > 0) {
    return { status: 'hidden' };
  }

  const projectIds = sample.projectIds ?? [];
  if (projectIds.length === 0) {
    return { status: 'resolved', visible: true };
  }

  if (optionsLoading) {
    return { status: 'loading' };
  }

  const missingIds = projectIdsMissingFromResolver(projectIds, resolveProject);
  if (missingIds.length === 0) {
    return {
      status: 'resolved',
      visible: isInHouseReportingCategoryContext(projectIds, resolveProject),
    };
  }

  return { status: 'fetch-missing', projectIds, missingIds };
}

/**
 * Whether the reporting-category field should show on sample details (tree).
 * Uses shared options cache (projects / projectType) when possible; project.get only for ids missing from cache.
 */
export function useSampleReportingCategoryVisible(
  sample: SampleDto | undefined,
  hasProcessDevAccess: boolean,
  projectOptions: SampleReportingCategoryProjectOptions
): { showReportingCategory: boolean; isReportingCategoryContextLoading: boolean } {
  const backend = useBackend();

  const resolveProject = useMemo(
    () => createProjectResolver(projectOptions.projects, projectOptions.projectTypes),
    [projectOptions.projects, projectOptions.projectTypes]
  );

  const evaluation = useMemo(
    () => evaluate(sample, hasProcessDevAccess, resolveProject, projectOptions.optionsLoading),
    [sample, hasProcessDevAccess, resolveProject, projectOptions.optionsLoading]
  );

  const fetchKey = evaluation.status === 'fetch-missing' ? evaluation.missingIds.join(',') : '';

  const [fetchResult, setFetchResult] = useState<{
    key: string;
    byId: ReadonlyMap<number, ProjectDto>;
  } | null>(null);

  useEffect(() => {
    if (evaluation.status !== 'fetch-missing') {
      return;
    }

    const key = evaluation.missingIds.join(',');
    let cancelled = false;

    void (async () => {
      try {
        const entries = await Promise.all(
          evaluation.missingIds.map(async (id) => {
            const project = await backend.project.get(String(id));
            return [id, project] as const;
          })
        );
        if (!cancelled) {
          setFetchResult({ key, byId: new Map(entries) });
        }
      } catch {
        if (!cancelled) {
          setFetchResult({ key, byId: new Map() });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [evaluation.status, fetchKey, backend, evaluation]);

  const isFetchPending = evaluation.status === 'fetch-missing' && (fetchResult == null || fetchResult.key !== fetchKey);

  if (evaluation.status === 'hidden') {
    return { showReportingCategory: false, isReportingCategoryContextLoading: false };
  }
  if (evaluation.status === 'loading' || isFetchPending) {
    return { showReportingCategory: false, isReportingCategoryContextLoading: true };
  }
  if (evaluation.status === 'resolved') {
    return {
      showReportingCategory: evaluation.visible,
      isReportingCategoryContextLoading: false,
    };
  }

  const visible = isInHouseReportingCategoryContext(evaluation.projectIds, resolveProject, fetchResult?.byId);
  return {
    showReportingCategory: visible,
    isReportingCategoryContextLoading: false,
  };
}
